/**
 * View and manage strings on a single string registry template (add/remove via Strapi `items`).
 * Compact locale summary column; edit overrides in a dialog.
 */

import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Breadcrumbs,
  Link,
  Alert,
  CircularProgress,
  Chip,
  useTheme,
  Tooltip,
  TextField,
  MenuItem
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import { DataGridPro, GridColDef, GridRenderCellParams, GridToolbar } from "@mui/x-data-grid-pro";
import { useGlobalConfig } from "context/GlobalContext";
import { BglPrimarySmallButton, BglSecondarySmallButton } from "components/button/BglButtons";
import { StringRegistryApis } from "../api/stringRegistryAPI";
import { StringTemplate } from "../types/stringRegistryTypes";
import { normalizeStringRegistryTemplateFromStrapi } from "../utils/strapiEntityNormalize";
import { AddStringsToTemplateDialog } from "../components/AddStringsToTemplateDialog";
import { EditTemplateLocaleOverridesDialog } from "../components/EditTemplateLocaleOverridesDialog";
import { ViewStringModal } from "../components/ViewStringModal";
import {
  normalizeLocaleCode,
  buildTemplateItemLocaleSummaryShort,
  buildTemplateItemLocaleTooltip
} from "../utils/localeDisplay";
import { SearchableSelect } from "components/common/SearchableSelect";

type PublishedLocale = { code: string; name: string };

export const StringTemplateDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { rc } = useGlobalConfig();
  const theme = useTheme();

  const [template, setTemplate] = useState<StringTemplate | null>(null);
  const [total, setTotal] = useState(0);
  const [facets, setFacets] = useState<{
    components: { documentId: string; code: string; displayName?: string }[];
    sections: { documentId: string; code: string; displayName?: string }[];
    overrideLocales: string[];
  }>({ components: [], sections: [], overrideLocales: [] });
  const [memberEntryIds, setMemberEntryIds] = useState<string[]>([]);
  const [localeCatalog, setLocaleCatalog] = useState<PublishedLocale[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [pageLoading, setPageLoading] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [viewIndex, setViewIndex] = useState<number | null>(null);
  const [deletingIndex, setDeletingIndex] = useState<number | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });

  // Server-side search + filters (search input is debounced before fetching)
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [overrideLocaleFilter, setOverrideLocaleFilter] = useState("");
  const [componentFilter, setComponentFilter] = useState("");
  const [sectionFilter, setSectionFilter] = useState("");
  const requestSeqRef = useRef(0);

  const canEdit = rc.permissionsManager.canTemplateEdit();

  // Debounce free-text search
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Published locales catalog (once)
  useEffect(() => {
    StringRegistryApis.getPublishedLocales(rc).then((locRes) => {
      if (!locRes.error) {
        const list = (locRes as { data?: unknown }).data;
        setLocaleCatalog(Array.isArray(list) ? (list as PublishedLocale[]) : []);
      } else {
        setLocaleCatalog([]);
      }
    });
  }, [rc]);

  // Fetch one page of template strings (server-side pagination + filters)
  const load = useCallback(async () => {
    if (!id) return;
    const seq = ++requestSeqRef.current;
    setPageLoading(true);
    setError(null);
    try {
      const { error: err, payload } = await StringRegistryApis.getTemplateValuesPage(rc, id, {
        page: paginationModel.page + 1,
        pageSize: paginationModel.pageSize,
        search: search || undefined,
        component: componentFilter || undefined,
        section: sectionFilter || undefined,
        overrideLocale: overrideLocaleFilter || undefined
      });
      if (seq !== requestSeqRef.current) return; // stale response

      if (err || !payload) {
        setError(err?.message || "Failed to load");
        setTemplate(null);
        return;
      }
      const data = {
        documentId: payload.templateId,
        name: payload.templateName,
        description: payload.description,
        module: payload.module,
        valueCount: payload.memberCount ?? payload.stringCount,
        items: (payload.strings || []).map((str: any) => ({
          stringRegistryEntry: {
            documentId: str.documentId,
            stringResourceId: str.stringResourceId,
            displayName: str.displayName,
            lookups: str.lookups,
            ...(Array.isArray(str.globalLocaleOverrides) && str.globalLocaleOverrides.length
              ? { templateItems: [{ localeOverrides: str.globalLocaleOverrides }] }
              : {})
          },
          localeOverrides: str.localeOverrides || []
        }))
      };
      setTemplate(normalizeStringRegistryTemplateFromStrapi(data));
      setTotal(payload.stringCount || 0);
      setFacets(payload.facets || { components: [], sections: [], overrideLocales: [] });
      setMemberEntryIds(payload.memberEntryIds || []);
    } catch (e: any) {
      if (seq === requestSeqRef.current) setError(e.message || "Error");
    } finally {
      if (seq === requestSeqRef.current) {
        setPageLoading(false);
        setLoading(false);
      }
    }
  }, [rc, id, paginationModel, search, componentFilter, sectionFilter, overrideLocaleFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const validLocaleCatalog = useMemo(
    () => localeCatalog.filter((l) => Boolean(normalizeLocaleCode(l?.code))),
    [localeCatalog]
  );

  const handleRemove = useCallback(
    async (index: number) => {
      if (!template) return;
      setDeletingIndex(index);
      setSaveError(null);
      try {
        const entryDocumentId = template.items?.[index]?.stringRegistryEntry?.documentId;
        if (!entryDocumentId) return;
        const res = await StringRegistryApis.removeTemplateValue(rc, template.documentId, entryDocumentId);
        if (res.error) {
          setSaveError(res.error.message || "Failed to remove string");
          return;
        }
        await load();
      } catch (e: any) {
        setSaveError(e.message || "Failed to remove string");
      } finally {
        setDeletingIndex(null);
      }
    },
    [rc, template, load]
  );

  const rows = useMemo(() => {
    return (
      template?.items?.map((item, index) => {
        const entry = item.stringRegistryEntry;
        const entryDocumentId = entry?.documentId;
        const ovLocales = (item.localeOverrides || [])
          .map((o) => normalizeLocaleCode(o.locale))
          .filter(Boolean);
        return {
          id: entryDocumentId ? `${entryDocumentId}-${index}` : `row-${index}`,
          index,
          entryDocumentId,
          stringResourceId: entry?.stringResourceId || "—",
          displayName: entry?.displayName || "—",
          components: entry?.components || [],
          sections: entry?.sections || [],
          overrideLocales: ovLocales,
          overrideSummary: ovLocales.length > 0 ? ovLocales.join(", ") : "—",
          localeSummary: buildTemplateItemLocaleSummaryShort(item, validLocaleCatalog, 160),
          localeTooltip: buildTemplateItemLocaleTooltip(item, validLocaleCatalog)
        };
      }) || []
    );
  }, [template, validLocaleCatalog]);

  // Filter options are template-wide facets from the server, not page-derived
  const overrideLocaleOptions = facets.overrideLocales;
  const componentOptions = facets.components;
  const sectionOptions = facets.sections;

  const filteredRows = rows;

  const hasActiveFilters = Boolean(
    search.trim() || overrideLocaleFilter || componentFilter || sectionFilter
  );

  useEffect(() => {
    setPaginationModel((m) => ({ ...m, page: 0 }));
  }, [search, overrideLocaleFilter, componentFilter, sectionFilter]);

  const columns: GridColDef[] = useMemo(() => {
    return [
      {
        field: "serialNo",
        headerName: "#",
        width: 64,
        sortable: false,
        align: "center",
        headerAlign: "center",
        renderCell: (params: GridRenderCellParams) => {
          const rowIndex = params.api.getRowIndexRelativeToVisibleRows(params.id);
          return (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
                height: "100%"
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 500, color: "text.secondary" }}>
                {paginationModel.page * paginationModel.pageSize + rowIndex + 1}
              </Typography>
            </Box>
          );
        }
      },
      { field: "stringResourceId", headerName: "stringResourceId", flex: 0.9, minWidth: 160 },
      { field: "displayName", headerName: "Display name", flex: 0.7, minWidth: 120 },
      {
        field: "localeSummary",
        headerName: "Locales (effective)",
        flex: 1.6,
        minWidth: 260,
        sortable: false,
        renderCell: (params: GridRenderCellParams) => (
          <Tooltip title={(params.row as { localeTooltip: string }).localeTooltip}>
            <Typography variant="body2" noWrap sx={{ maxWidth: "100%" }}>
              {params.value as string}
            </Typography>
          </Tooltip>
        )
      },
      {
        field: "overrideSummary",
        headerName: "Override locales",
        width: 140,
        sortable: false,
        renderCell: (params: GridRenderCellParams) => (
          <Tooltip title="Locales where this template supplies text instead of the global string">
            <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: "100%" }}>
              {params.value as string}
            </Typography>
          </Tooltip>
        )
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 156,
        sortable: false,
        filterable: false,
        renderCell: (params: GridRenderCellParams) => {
          const row = params.row as {
            index: number;
            entryDocumentId?: string;
          };
          const isDeleting = deletingIndex === row.index;
          return (
            <Box sx={{ display: "flex", gap: 0.5 }}>
              {row.entryDocumentId && (
                <Tooltip title="View string">
                  <IconButton size="small" onClick={() => setViewIndex(row.index)}>
                    <VisibilityIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {canEdit && (
                <>
                  <Tooltip title="Edit template overrides">
                    <IconButton size="small" onClick={() => setEditIndex(row.index)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Remove from template">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleRemove(row.index)}
                      disabled={isDeleting}
                    >
                      {isDeleting ? <CircularProgress size={16} /> : <DeleteIcon fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                </>
              )}
            </Box>
          );
        }
      }
    ];
  }, [deletingIndex, canEdit, handleRemove, paginationModel]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, maxWidth: "100%", mx: "auto" }}>
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          component="button"
          variant="body2"
          onClick={() => navigate("/cms/string-registry?tab=templates")}
          sx={{ cursor: "pointer" }}
        >
          String Registry
        </Link>
        <Typography color="text.primary">Template</Typography>
      </Breadcrumbs>

      <Paper sx={{ p: 2, border: `1px solid ${theme.palette.divider}` }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, mb: 2, flexWrap: "wrap" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconButton size="small" onClick={() => navigate("/cms/string-registry?tab=templates")}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h5" fontWeight={600}>
              {template?.name || "Template"}
            </Typography>
            {template?.module && <Chip size="small" label={template.module} />}
          </Box>
          {canEdit && template && (
            <BglPrimarySmallButton startIcon={<AddIcon />} onClick={() => setAddOpen(true)}>
              Add strings
            </BglPrimarySmallButton>
          )}
        </Box>

        {template?.description && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {template.description}
          </Typography>
        )}

        <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
          <Typography variant="body2">
            One column summarizes all locales (template value when set, otherwise global). Use the pencil to edit
            overrides. Catalog: GET /string-registry/v1/locales.
          </Typography>
        </Alert>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {saveError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSaveError(null)}>
            {saveError}
          </Alert>
        )}

        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Strings in template ({rows.length})
        </Typography>

        {/* Search + filters (local, over template items) */}
        <Box sx={{ display: "flex", gap: 1.5, mb: 2, flexWrap: "wrap", alignItems: "center" }}>
          <TextField
            size="small"
            label="Search"
            placeholder="stringResourceId or display name…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            sx={{ minWidth: 260 }}
          />
          <SearchableSelect
            size="small"
            sx={{ minWidth: 180 }}
            label="Component"
            value={componentFilter}
            onChange={setComponentFilter}
            options={[{ value: "", label: "All components" }, ...componentOptions.map((c) => ({ value: c.documentId, label: c.displayName || c.code }))]}
          />
          <SearchableSelect
            size="small"
            sx={{ minWidth: 200 }}
            label="Section"
            value={sectionFilter}
            onChange={setSectionFilter}
            options={[{ value: "", label: "All sections" }, ...sectionOptions.map((sec) => ({ value: sec.documentId, label: sec.displayName || sec.code }))]}
          />
          <SearchableSelect
            size="small"
            sx={{ minWidth: 200 }}
            label="Override locale"
            value={overrideLocaleFilter}
            onChange={setOverrideLocaleFilter}
            options={[
              { value: "", label: "All strings" },
              { value: "__none__", label: "No overrides" },
              ...overrideLocaleOptions.map((loc) => ({ value: loc, label: `Has override: ${loc}` }))
            ]}
          />
          {hasActiveFilters && (
            <BglSecondarySmallButton
              onClick={() => {
                setSearchInput("");
                setSearch("");
                setOverrideLocaleFilter("");
                setComponentFilter("");
                setSectionFilter("");
              }}
            >
              Clear
            </BglSecondarySmallButton>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ ml: "auto" }}>
            Showing {rows.length} of {total} strings
          </Typography>
        </Box>

        <Box
          sx={{
            width: "100%",
            height: { xs: "calc(100vh - 460px)", sm: "calc(100vh - 420px)" },
            minHeight: 400,
            overflow: "hidden"
          }}
        >
          <DataGridPro
            rows={filteredRows}
            columns={columns}
            disableRowSelectionOnClick
            pagination
            paginationMode="server"
            rowCount={total}
            loading={pageLoading}
            pageSizeOptions={[25, 50, 100]}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            slots={{ toolbar: GridToolbar }}
            slotProps={{ toolbar: { showQuickFilter: false } }}
            sx={{
              minWidth: 640,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 1,
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor:
                  theme.palette.mode === "dark" ? "rgba(255,255,255,0.06)" : "#f8f9fa",
                borderBottom: `2px solid ${theme.palette.divider}`,
                "& .MuiDataGrid-columnHeaderTitle": {
                  fontWeight: 600,
                  fontSize: "0.8rem",
                  textTransform: "uppercase",
                  letterSpacing: "0.4px",
                  color: theme.palette.text.secondary
                }
              },
              "& .MuiDataGrid-cell": {
                display: "flex",
                alignItems: "center",
                borderBottom: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)"}`
              },
              "& .MuiDataGrid-row:hover": {
                bgcolor:
                  theme.palette.mode === "dark"
                    ? "rgba(25,118,210,0.1)"
                    : "rgba(25,118,210,0.05)"
              },
              "& .MuiDataGrid-footerContainer": {
                borderTop: `2px solid ${theme.palette.divider}`,
                bgcolor:
                  theme.palette.mode === "dark" ? "rgba(255,255,255,0.03)" : "#f8f9fa"
              }
            }}
          />
        </Box>
      </Paper>

      {template && (
        <AddStringsToTemplateDialog
          open={addOpen}
          onClose={() => setAddOpen(false)}
          template={template}
          memberEntryIds={memberEntryIds}
          onStringsAdded={load}
        />
      )}

      {template && canEdit && (
        <EditTemplateLocaleOverridesDialog
          open={editIndex !== null}
          onClose={() => setEditIndex(null)}
          template={template}
          itemIndex={editIndex}
          localeCatalog={localeCatalog}
          onSaved={load}
        />
      )}

      <ViewStringModal
        open={viewIndex !== null}
        onClose={() => setViewIndex(null)}
        entry={
          viewIndex !== null ? template?.items?.[viewIndex]?.stringRegistryEntry || null : null
        }
        localeOverrides={viewIndex !== null ? template?.items?.[viewIndex]?.localeOverrides : undefined}
      />
    </Box>
  );
};

export default StringTemplateDetailPage;
